import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Newspaper } from 'lucide-react';
import api from '../../services/api';
import { assetUrl } from '../../utils/assets';

const HomepageBlog = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const scrollContainerRef = useRef(null);
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(true);

  useEffect(() => {
    fetchPosts();
  }, []);

  const fetchPosts = async () => {
    try {
      const response = await api.get('/blog/homepage/posts');
      setPosts(response.data);
    } catch (error) {
      console.error('Error fetching homepage posts:', error);
    } finally {
      setLoading(false);
    }
  };

  // Resolve stored paths through the shared asset helper so images work in
  // every environment instead of being pinned to the production hostname.
  const getImageUrl = assetUrl;

  const truncateText = (text, length = 100) => {
    if (!text) return '';
    if (text.length <= length) return text;
    return text.substring(0, length) + '...';
  };

  // Check scroll position to show/hide arrows
  const checkScrollPosition = () => {
    if (scrollContainerRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollContainerRef.current;
      setShowLeftArrow(scrollLeft > 10);
      setShowRightArrow(scrollLeft + clientWidth < scrollWidth - 10);
    }
  };

  // Auto-scroll functionality for mobile
  useEffect(() => {
    if (!scrollContainerRef.current || posts.length <= 2) return;

    let scrollInterval;
    const container = scrollContainerRef.current;

    const startAutoScroll = () => {
      scrollInterval = setInterval(() => {
        if (container && window.innerWidth < 768) {
          const maxScroll = container.scrollWidth - container.clientWidth;
          if (container.scrollLeft >= maxScroll - 10) {
            container.scrollTo({ left: 0, behavior: 'smooth' });
          } else {
            container.scrollBy({ left: 320, behavior: 'smooth' });
          }
          setTimeout(checkScrollPosition, 500);
        }
      }, 5000);
    };

    const stopAutoScroll = () => {
      if (scrollInterval) clearInterval(scrollInterval);
    };

    startAutoScroll();

    container.addEventListener('mouseenter', stopAutoScroll);
    container.addEventListener('touchstart', stopAutoScroll);
    container.addEventListener('mouseleave', startAutoScroll);
    container.addEventListener('touchend', startAutoScroll);
    container.addEventListener('scroll', checkScrollPosition);

    checkScrollPosition();

    return () => {
      stopAutoScroll();
      if (container) {
        container.removeEventListener('mouseenter', stopAutoScroll);
        container.removeEventListener('touchstart', stopAutoScroll);
        container.removeEventListener('mouseleave', startAutoScroll);
        container.removeEventListener('touchend', startAutoScroll);
        container.removeEventListener('scroll', checkScrollPosition);
      }
    };
  }, [posts]);

  const scroll = (direction) => {
    if (scrollContainerRef.current) {
      const scrollAmount = 320;
      const newScrollLeft = scrollContainerRef.current.scrollLeft + (direction === 'left' ? -scrollAmount : scrollAmount);
      scrollContainerRef.current.scrollTo({ left: newScrollLeft, behavior: 'smooth' });
      setTimeout(checkScrollPosition, 500);
    }
  };

  if (loading) return null;
  if (posts.length === 0) return null;

  const latestPost = posts[0];
  const otherPosts = posts.slice(1);

  return (
    <section className="section relative overflow-hidden">
      {/* Subtle branded backdrop — no clashing gradient stops. */}
      <div className="absolute inset-0 bg-navy-fade" aria-hidden="true" />
      <div
        className="absolute inset-0 opacity-[0.06]"
        style={{
          backgroundImage:
            'radial-gradient(circle at 1px 1px, #ffffff 1px, transparent 0)',
          backgroundSize: '24px 24px',
        }}
        aria-hidden="true"
      />

      <div className="container-page relative z-10">
        {/* Left aligned header */}
        <div className="mb-8">
          <span className="eyebrow bg-white/10 text-gold-300">Stay Updated</span>
          <h2 className="mt-3 text-2xl font-bold text-white md:text-3xl">
            Latest News &amp; Updates
          </h2>
          <p className="mt-2 max-w-2xl text-sm text-white/70 md:text-base">
            Stay informed with the latest happenings at Katine TTI
          </p>
          <div className="mt-4 h-1 w-16 rounded-full bg-gold-400" aria-hidden="true" />
        </div>

        {/* Desktop Layout: 3 columns */}
        <div className="hidden md:grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {posts.slice(0, 3).map((post) => (
            <Link
              key={post.id}
              to={`/blog/${post.slug}`}
              className="group transform transition-all duration-300 hover:-translate-y-1"
            >
              <div className="h-full overflow-hidden rounded-xl bg-white shadow-card transition-shadow duration-300 group-hover:shadow-card-hover">
                <div className="relative h-44 overflow-hidden">
                  <div className="absolute inset-0 flex items-center justify-center bg-brand-800">
                    <Newspaper className="h-9 w-9 text-white/40" aria-hidden="true" />
                  </div>
                  {getImageUrl(post.featured_image) ? (
                    <img
                      src={getImageUrl(post.featured_image)}
                      alt=""
                      loading="lazy"
                      className="relative h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                      onError={(e) => {
                        e.currentTarget.style.display = 'none';
                      }}
                    />
                  ) : null}
                  {post.category_name && (
                    <span className="absolute top-3 left-3 px-2 py-0.5 bg-[#fbbf24] text-gray-900 text-xs font-semibold rounded-full">
                      {post.category_name}
                    </span>
                  )}
                  <div className="absolute bottom-3 right-3 px-2 py-0.5 bg-black/50 rounded-md backdrop-blur-sm">
                    <span className="text-white text-xs">
                      {new Date(post.published_at).toLocaleDateString()}
                    </span>
                  </div>
                </div>
                <div className="p-4">
                  <h3 className="font-bold text-gray-800 text-base mb-1 line-clamp-2 group-hover:text-primary transition-colors">
                    {post.title}
                  </h3>
                  <p className="text-gray-600 text-xs line-clamp-2">
                    {post.excerpt || truncateText(post.content, 80)}
                  </p>
                  <div className="mt-3 flex items-center justify-between">
                    <span className="text-[#fbbf24] text-xs font-medium group-hover:underline flex items-center gap-1">
                      Read More
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </span>
                    <span className="text-xs text-gray-400 flex items-center gap-1">
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                      </svg>
                      {post.view_count || 0}
                    </span>
                  </div>
                </div>
              </div>
            </Link>
          ))}
        </div>

        {/* Mobile Layout: Featured post + scrollable cards */}
        <div className="md:hidden">
          {/* Featured Latest Post - Full width */}
          {latestPost && (
            <Link
              to={`/blog/${latestPost.slug}`}
              className="block mb-6 group"
            >
              <div className="overflow-hidden rounded-xl bg-white shadow-card transition-shadow duration-300 group-hover:shadow-card-hover">
                <div className="relative h-56 overflow-hidden">
                  <div className="absolute inset-0 flex items-center justify-center bg-brand-800">
                    <Newspaper className="h-11 w-11 text-white/40" aria-hidden="true" />
                  </div>
                  {getImageUrl(latestPost.featured_image) ? (
                    <img
                      src={getImageUrl(latestPost.featured_image)}
                      alt=""
                      loading="lazy"
                      className="relative h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                      onError={(e) => {
                        e.currentTarget.style.display = 'none';
                      }}
                    />
                  ) : null}
                  {latestPost.category_name && (
                    <span className="absolute top-3 left-3 px-2 py-0.5 bg-[#fbbf24] text-gray-900 text-xs font-semibold rounded-full">
                      {latestPost.category_name}
                    </span>
                  )}
                  <div className="absolute bottom-3 right-3 px-2 py-0.5 bg-black/50 rounded-md backdrop-blur-sm">
                    <span className="text-white text-xs">
                      {new Date(latestPost.published_at).toLocaleDateString()}
                    </span>
                  </div>
                </div>
                <div className="p-4">
                  <h3 className="font-bold text-gray-800 text-lg mb-2 line-clamp-2 group-hover:text-primary transition-colors">
                    {latestPost.title}
                  </h3>
                  <p className="text-gray-600 text-sm line-clamp-3">
                    {latestPost.excerpt || truncateText(latestPost.content, 120)}
                  </p>
                  <div className="mt-3 flex items-center justify-between">
                    <span className="text-[#fbbf24] text-sm font-medium group-hover:underline flex items-center gap-1">
                      Read More
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </span>
                    <span className="text-xs text-gray-400 flex items-center gap-1">
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                      </svg>
                      {latestPost.view_count || 0}
                    </span>
                  </div>
                </div>
              </div>
            </Link>
          )}

          {/* Scrollable Other Posts */}
          {otherPosts.length > 0 && (
            <div className="relative">
              <div className="flex justify-between items-center mb-3">
                <h3 className="text-white text-sm font-semibold">More News</h3>
              </div>

              {/* Centered Arrow Buttons */}
              <div className="relative">
                {showLeftArrow && (
                  <button
                    onClick={() => scroll('left')}
                    className="absolute left-0 top-1/2 -translate-y-1/2 z-10 w-8 h-8 rounded-full bg-[#fbbf24] text-gray-900 flex items-center justify-center hover:bg-[#e6a812] shadow-lg transition-all duration-300"
                    style={{ transform: 'translateY(-50%)' }}
                  >
                    ←
                  </button>
                )}

                <div
                  ref={scrollContainerRef}
                  className="flex overflow-x-auto gap-4 pb-4 scroll-smooth"
                  style={{
                    scrollbarWidth: 'thin',
                    WebkitOverflowScrolling: 'touch',
                    scrollbarColor: '#fbbf24 #f0f0f0'
                  }}
                >
                  {otherPosts.map((post) => (
                    <Link
                      key={post.id}
                      to={`/blog/${post.slug}`}
                      className="flex-shrink-0 w-[calc(50%-8px)] min-w-[160px] group transform transition-all duration-300 hover:-translate-y-1"
                      style={{ width: 'calc(50% - 8px)' }}
                    >
                      <div className="h-full overflow-hidden rounded-xl bg-white shadow-card transition-shadow duration-300 group-hover:shadow-card-hover">
                        <div className="relative h-32 overflow-hidden">
                          <div className="absolute inset-0 flex items-center justify-center bg-brand-800">
                            <Newspaper className="h-7 w-7 text-white/40" aria-hidden="true" />
                          </div>
                          {getImageUrl(post.featured_image) ? (
                            <img
                              src={getImageUrl(post.featured_image)}
                              alt=""
                              loading="lazy"
                              className="relative h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                              onError={(e) => {
                                e.currentTarget.style.display = 'none';
                              }}
                            />
                          ) : null}
                          {post.category_name && (
                            <span className="absolute top-2 left-2 px-1.5 py-0.5 bg-[#fbbf24] text-gray-900 text-[10px] font-semibold rounded-full">
                              {post.category_name}
                            </span>
                          )}
                          <div className="absolute bottom-2 right-2 px-1.5 py-0.5 bg-black/50 rounded-md backdrop-blur-sm">
                            <span className="text-white text-[9px]">
                              {new Date(post.published_at).toLocaleDateString()}
                            </span>
                          </div>
                        </div>
                        <div className="p-2">
                          <h3 className="font-bold text-gray-800 text-xs mb-1 line-clamp-2 group-hover:text-primary transition-colors">
                            {post.title}
                          </h3>
                          <p className="text-gray-600 text-[10px] line-clamp-2">
                            {post.excerpt || truncateText(post.content, 50)}
                          </p>
                          <div className="mt-2 flex items-center justify-between">
                            <span className="text-[#fbbf24] text-[10px] font-medium group-hover:underline flex items-center gap-0.5">
                              Read
                              <svg className="w-2 h-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                              </svg>
                            </span>
                            <span className="text-[10px] text-gray-400 flex items-center gap-0.5">
                              <svg className="w-2 h-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                              </svg>
                              {post.view_count || 0}
                            </span>
                          </div>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>

                {showRightArrow && (
                  <button
                    onClick={() => scroll('right')}
                    className="absolute right-0 top-1/2 -translate-y-1/2 z-10 w-8 h-8 rounded-full bg-[#fbbf24] text-gray-900 flex items-center justify-center hover:bg-[#e6a812] shadow-lg transition-all duration-300"
                    style={{ transform: 'translateY(-50%)' }}
                  >
                    →
                  </button>
                )}
              </div>
            </div>
          )}
        </div>

        <div className="text-center mt-8">
          <Link
            to="/blog"
            className="inline-flex items-center gap-2 px-5 py-2 bg-[#fbbf24] text-gray-900 rounded-lg hover:bg-[#e6a812] transition-all duration-300 font-medium shadow-md hover:shadow-lg text-sm"
          >
            View All News
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
            </svg>
          </Link>
        </div>
      </div>
    </section>
  );
};

export default HomepageBlog;
